import { useCallback, useEffect, useState } from 'react'
import { getGroupRevision, getGroupSnapshot, type GroupSnapshot } from '../../../types'
import { actionApiCall } from '../api/actionApiCall'
import { readCachedSnapshot, writeCachedSnapshot } from '../offline/snapshotCache'
import { useOnlineStatus } from '../offline/useOnlineStatus'

export type GroupSnapshotState = {
    snapshot: GroupSnapshot | undefined
    /** Zobrazená data pocházejí z lokální cache, ne z čerstvého volání serveru. */
    fromCache: boolean
    /** Kdy byla data naposledy stažena ze serveru. */
    syncedAt: string | undefined
    loading: boolean
    refreshing: boolean
    /** Data ani v cache nejsou a server je nedostupný. */
    failed: boolean
    online: boolean
    /** Znovu načte stav ze serveru; `force` obejde kontrolu revize. */
    refresh: (force?: boolean) => Promise<void>
}

/**
 * Stav skupiny s lokální cache.
 *
 * Postup: nejdřív se zobrazí uložený snímek (aplikace naskočí okamžitě),
 * pak se levným dotazem ověří revize a jen při neshodě se stahuje znovu.
 * Tlačítko „Načíst znovu" projde vždy, ať se dá vynutit čerstvý stav.
 */
export function useGroupSnapshot(groupId: string): GroupSnapshotState {
    const online = useOnlineStatus()

    const [snapshot, setSnapshot] = useState<GroupSnapshot | undefined>()
    const [syncedAt, setSyncedAt] = useState<string | undefined>()
    const [fromCache, setFromCache] = useState(false)
    const [loading, setLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)
    const [failed, setFailed] = useState(false)

    const download = useCallback(async () => {
        const fresh = await actionApiCall(() => getGroupSnapshot({ path: { groupId } }), { silent: true })
        if (!fresh) {
            return false
        }

        setSnapshot(fresh)
        setSyncedAt(fresh.generatedAt)
        setFromCache(false)
        await writeCachedSnapshot(groupId, fresh)
        return true
    }, [groupId])

    const refresh = useCallback(
        async (force = false) => {
            if (!groupId) {
                return
            }

            setRefreshing(true)
            try {
                if (!force && snapshot) {
                    // Levný dotaz: když se revize nezměnila, není co stahovat.
                    const revision = await actionApiCall(() => getGroupRevision({ path: { groupId } }), { silent: true })
                    if (revision && revision.revision === snapshot.group.revision) {
                        setFromCache(false)
                        setFailed(false)
                        return
                    }
                    if (!revision) {
                        setFailed(!snapshot)
                        return
                    }
                }

                const ok = await download()
                setFailed(!ok && !snapshot)
            } finally {
                setRefreshing(false)
            }
        },
        [download, groupId, snapshot],
    )

    // První načtení: cache okamžitě, pak ověření proti serveru.
    useEffect(() => {
        if (!groupId) {
            return
        }

        let cancelled = false
        setLoading(true)
        setFailed(false)

        void (async () => {
            const cached = await readCachedSnapshot(groupId)
            if (cancelled) {
                return
            }

            if (cached) {
                setSnapshot(cached.snapshot)
                setSyncedAt(cached.storedAt)
                setFromCache(true)
            }
            setLoading(false)

            const ok = await download()
            if (!cancelled && !ok) {
                setFailed(!cached)
            }
        })()

        return () => {
            cancelled = true
        }
    }, [download, groupId])

    // Po obnovení připojení se stav dorovná sám.
    useEffect(() => {
        if (online && snapshot && fromCache) {
            void refresh()
        }
        // Záměrně jen při změně připojení, ne při každém překreslení.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [online])

    return { snapshot, fromCache, syncedAt, loading, refreshing, failed, online, refresh }
}
